import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  getGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompanyPaginatedQueryKey,
  useGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompanyPaginated,
  usePatchApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompanyId,
} from '@/api/generated/company-management-user/company-management-user'
import { AbsenceApproval } from '@/api/model'
import type { AbsenceModel, AbsenceModelPaginatedResponse, OrderType } from '@/api/model'
import { notify } from '@/lib/notify'
import { getAbsenceStatusLabel } from '../all-entries-components/absenceLabels'

const DEFAULT_PAGE_SIZE = 10

/** Active filters of the absence list (employee optional, date range as ISO). */
export interface AbsenceEntriesFilters {
  employeeUserId?: string
  from?: string
  to?: string
  status?: AbsenceApproval
}

/** Unwrap the query response from the axios wrapper if needed (hooks return the full response). */
function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

/**
 * Data layer of the absence list: server-side pagination + sorting as well as
 * approving/rejecting individual entries. Table state (sort/page/size) lives
 * here; the filters come from outside (so that e.g. the calendar can share the same
 * employee filter). Used by `AbsenceAllEntriesPage` and `AbsenceEmployeeEntriesPage`.
 */
export function useAbsenceEntries(serviceModuleId: number, filters: AbsenceEntriesFilters) {
  const { t } = useTranslation('service-modules-absence')
  const { employeeUserId, from, to, status } = filters
  const queryClient = useQueryClient()

  const [sort, setSort] = useState<string>()
  const [order, setOrder] = useState<OrderType>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  // Filter change → back to the first page.
  useEffect(() => {
    setOffset(0)
  }, [employeeUserId, from, to, status])

  const listQuery = useGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompanyPaginated(
    serviceModuleId,
    {
      Offset: offset,
      Limit: limit,
      Sort: sort,
      Order: order,
      employeeUserId,
      startDate: from ? dayjs(from).format('YYYY-MM-DD') : undefined,
      endDate: to ? dayjs(to).format('YYYY-MM-DD') : undefined,
      status,
    },
  )
  const list = unwrap<AbsenceModelPaginatedResponse>(listQuery.data)

  const patch = usePatchApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompanyId()

  async function updateStatus(absence: AbsenceModel, status: AbsenceApproval) {
    if (absence.absenceApproval === status) return
    setUpdatingId(absence.id)
    try {
      await patch.mutateAsync({ serviceModuleId, id: absence.id, data: { absenceApproval: status } })
      await queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompanyPaginatedQueryKey(serviceModuleId),
      })
      notify.success(t('absence.statusToast', { status: getAbsenceStatusLabel(t, status) }))
    } catch (error) {
      notify.apiError(error, t('absence.updateError'))
    } finally {
      setUpdatingId(null)
    }
  }

  return {
    rows: list?.items,
    totalCount: list?.totalCount,
    isLoading: listQuery.isLoading || listQuery.isFetching,
    isError: listQuery.isError,
    sort,
    order,
    onSort: (newSort?: string, newOrder?: OrderType) => {
      setSort(newSort)
      setOrder(newOrder)
      setOffset(0)
    },
    offset,
    limit,
    onOffsetChange: setOffset,
    onPageSizeChange: (pageSize: number) => {
      setLimit(pageSize)
      setOffset(0)
    },
    updatingId,
    updateStatus,
  }
}

export type AbsenceEntriesController = ReturnType<typeof useAbsenceEntries>
